/**
 * PublishButton — Fixed publish button with loading/success/error states.
 *
 * Shows "Published" (disabled) when clean, "Publish" when dirty,
 * spinner when publishing, and last-published timestamp below.
 */

import { Loader2 } from 'lucide-react'

interface PublishButtonProps {
  isDirty: boolean
  isPublishing: boolean
  lastPublishedAt?: string | null
  onClick: () => void
}

export function PublishButton({
  isDirty,
  isPublishing,
  lastPublishedAt,
  onClick,
}: PublishButtonProps) {
  return (
    <div className="flex flex-col items-end gap-0.5">
      <button
        onClick={onClick}
        disabled={isPublishing || !isDirty}
        className="flex items-center gap-1.5 rounded-sm bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:bg-gray-200 disabled:text-gray-400"
      >
        {isPublishing && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
        {isPublishing ? 'Publishing...' : isDirty ? 'Publish' : 'Published'}
      </button>
      {lastPublishedAt && !isDirty && (
        <span className="text-[11px] text-gray-400">
          {formatTimestamp(lastPublishedAt)}
        </span>
      )}
    </div>
  )
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return ''
  }
}
